import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Logo from '../Logo/White/Full/1Asset 13@72x-8.png';
import image from './image.png'; // QR code image

const OnlineClasses = () => {
  const pdfRef = useRef();
  const [isExporting, setIsExporting] = useState(false);

  const subjects = [
    'Artificial Intelligence',
    'Machine Learning',
    'Data Science',
    'Cybersecurity',
  ];

  const downloadPdf = async () => {
    setIsExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 100));
    const element = pdfRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const ratio = imgProps.width / imgProps.height;
    const width = pageWidth;
    const height = width / ratio;
    const yOffset = (pageHeight - height) / 2;

    pdf.addImage(imgData, 'PNG', 0, yOffset, width, height);

    // ✅ Add centered clickable link directly below the QR code
    const linkText = 'https://chat.whatsapp.com/BsZBvFsIyXu9tkyeFLdB47';
    const fontSize = 12;
    pdf.setFontSize(fontSize);
    pdf.setTextColor(0, 102, 204); // Blue color

    const textWidth = pdf.getTextWidth(linkText);
    const centerX = (pageWidth - textWidth) / 2;

    const linkY = yOffset + height - 20; // Approximate position near bottom QR section

    pdf.textWithLink(linkText, centerX, linkY, {
      url: linkText,
    });

    pdf.save('OnlineClasses.pdf');
    setIsExporting(false);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f9f9f9', minHeight: '100vh' }}>
      <Button
        variant="contained"
        onClick={downloadPdf}
        sx={{
          mb: 2,
          backgroundColor: '#007BFF',
          '&:hover': { backgroundColor: '#0056b3' },
        }}
      >
        Download as PDF
      </Button>

      <Box
        ref={pdfRef}
        sx={{
          maxWidth: '794px',
          margin: 'auto',
          padding: 4,
          backgroundColor: '#ffffff',
          color: '#000000',
          borderRadius: 3,
          boxShadow: 3,
          border: '1px solid #ddd',
          userSelect: isExporting ? 'none' : 'auto',
        }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 10 }}>
          <img src={Logo} alt="Logo" style={{ maxWidth: '500px' }} />
        </Box>

        {/* Title */}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 10,
            letterSpacing: '3px',
            color: isExporting ? '#6A0DAD' : 'transparent',
            backgroundImage: isExporting
              ? 'none'
              : 'linear-gradient(90deg, #007BFF, #ff4081)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: isExporting ? '#6A0DAD' : 'transparent',
          }}
        >
          Online Live Classes by NITK Alumni
        </Typography>

        {/* Subjects and Class Details */}
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 3,
                backgroundColor: '#f1f1f1',
                borderRadius: 2,
                borderLeft: '5px solid #007BFF',
                height: '100%',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#007BFF',
                  fontWeight: 'bold',
                  mb: 2,
                  letterSpacing: '2px',
                }}
              >
                📚 Subjects Covered
              </Typography>
              <List dense>
                {subjects.map((subj, i) => (
                  <ListItem key={i} sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#007BFF', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={subj}
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        letterSpacing: '0.5px',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 3,
                backgroundColor: '#f1f1f1',
                borderRadius: 2,
                borderLeft: '5px solid #007BFF',
                height: '100%',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#007BFF',
                  fontWeight: 'bold',
                  mb: 3,
                  letterSpacing: '2px',
                }}
              >
                🎓 Class Details
              </Typography>
              <Typography
                sx={{
                  mb: 2,
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: 'black',
                  letterSpacing: '0.5px',
                }}
              >
                🗣 <strong>Languages:</strong> English, Kannada, Hindi
              </Typography>
              <Typography
                sx={{
                  mb: 2,
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: 'black',
                  letterSpacing: '0.5px',
                }}
              >
                ⏰ <strong>Time:</strong> 1 hour daily
              </Typography>
              <Typography
                sx={{
                  mb: 2,
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: 'black',
                  letterSpacing: '0.5px',
                }}
              >
                💰 <strong>Fee:</strong> ₹500/month or ₹17/day
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: 'black',
                  letterSpacing: '0.5px',
                }}
              >
                🎯 <strong>Note:</strong> Limited seats available
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Motivation + QR Code */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 7,
              letterSpacing: '1px',
              color: '#FF8C00',
              fontWeight: 'bold',
              fontStyle: 'italic',
              fontFamily: '"Comic Sans MS", cursive, sans-serif',
            }}
          >
            🚀 "Empower your future — start learning today!"
          </Typography>

          <img
            src={image}
            alt="QR Code"
            style={{
              width: 140,
              height: 140,
              borderRadius: 10,
              border: '2px solid #4169E1',
              marginBottom: 30,
            }}
          />

          <Typography
            variant="h6"
            sx={{
              color: '#28a745',
              fontWeight: 'bold',
              letterSpacing: '1px',
              mb: 5,
              fontFamily: '"Comic Sans MS", cursive, sans-serif',
            }}
          >
            📱 Scan to Join WhatsApp Group
          </Typography>

          <Typography variant="body1" sx={{ color: 'black' }}>
            📞 Contact: <strong>+91-7019496010</strong>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OnlineClasses;
